import { useEffect, useMemo, useState } from 'react';
import { color } from '../styles/theme';
import { dateTime, num } from '../lib/format';
import { useMeta } from '../api/queries';
import {
  useDictionary,
  useDownloadExport,
  useExportMap,
  useExportPlan,
  useRedcapSetup,
  useSaveExportMap,
  useSuggestMap,
  type ExportFilterInput,
  type MapInputRow,
} from '../api/redcap';
import type { RedcapDictionaryField, RedcapSource } from '../api/client';
import { Button, Card, CardTitle, ColHead, ErrorNote, Eyebrow, Field, Loading, inputStyle, selectStyle } from '../components/ui';
import { IconDownload } from '../components/icons';
import { GuideCard, MONO, Note, Pill, PointerCard, ProblemNote, Where, problemOf } from './RedcapShared';
import { DEFAULT_EXPORT_FILTER, type RedcapTab } from './RedcapSteps';

/**
 * Export to REDCap — website sign-ups, written in the study's REDCap project's
 * own variables, for a coordinator to upload with REDCap's Data Import Tool.
 *
 * The lab moves data by hand, and REDCap accepts a file only in its own terms:
 * the record ID first, then variable names from the project's dictionary,
 * choices as codes. The map says which CRM field fills which variable; the
 * export does the rest, and remembers who it sent so nobody goes twice.
 *
 * The file comes first on the tab and the map second: the file is what a
 * coordinator comes for, week after week, and the map is set up once. When the
 * map is not ready the plan says so and names it, so nothing is lost by the
 * order.
 */

interface DraftRow {
  variable: string;
  source: string;
  /** "CRM value = code" lines. */
  valueText: string;
}

const CHOICE_TYPES = new Set(['radio', 'dropdown', 'checkbox', 'yesno', 'truefalse']);
const MAPPABLE = new Set(['text', 'notes', 'radio', 'dropdown', 'checkbox', 'yesno', 'truefalse']);

function toText(map: Record<string, string> | null | undefined): string {
  return map ? Object.entries(map).map(([k, v]) => `${k} = ${v}`).join('\n') : '';
}

/** "Female = 1" lines → { Female: '1' }; blank lines skipped; the first `=` splits. */
function parseValueText(text: string): Record<string, string> | null {
  const out: Record<string, string> = {};
  for (const line of text.split('\n')) {
    const at = line.indexOf('=');
    if (at < 0) continue;
    const from = line.slice(0, at).trim();
    const to = line.slice(at + 1).trim();
    if (from && to) out[from] = to;
  }
  return Object.keys(out).length > 0 ? out : null;
}

function sourceGroups(sources: RedcapSource[]): Array<[string, RedcapSource[]]> {
  return [
    ['Intake form', sources.filter((s) => !s.key.startsWith('answer:') && s.forms.includes('intake'))],
    [
      'Clinician referral form',
      sources.filter((s) => !s.key.startsWith('answer:') && !s.forms.includes('intake') && s.forms.includes('referral')),
    ],
    ['Study questions', sources.filter((s) => s.key.startsWith('answer:'))],
  ];
}

const MAP_GRID = 'minmax(0,1.5fr) minmax(0,1.2fr) minmax(0,1.5fr) auto';

/** The map is the lab's (043): every study's project is built the same, so every study exports through it. */
function MapEditor({ canWrite }: { canWrite: boolean }) {
  const map = useExportMap();
  const dict = useRedcapSetup();
  const save = useSaveExportMap();
  const suggest = useSuggestMap();

  const [draft, setDraft] = useState<DraftRow[] | null>(null);
  const [addForm, setAddForm] = useState('');
  const [addVar, setAddVar] = useState('');
  const [suggestForms, setSuggestForms] = useState<string[]>([]);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  // Start from the saved map, and follow it until someone edits.
  const saved = useMemo<DraftRow[]>(
    () => (map.data?.rows ?? []).map((r) => ({ variable: r.variable, source: r.source, valueText: toText(r.valueMap) })),
    [map.data],
  );
  useEffect(() => {
    setDraft(null);
  }, [map.data]);
  const rows = draft ?? saved;
  const dirty = draft !== null;

  const fields = dict.data?.fields ?? [];
  const byVar = useMemo(() => new Map(fields.map((f) => [f.variable, f])), [fields]);
  const recordId = dict.data?.setup.recordIdField ?? null;
  const forms = dict.data?.summary.forms ?? [];
  const sources = map.data?.sources ?? [];
  const sourceByKey = useMemo(() => new Map(sources.map((s) => [s.key, s])), [sources]);

  if (map.isLoading || dict.isLoading) return <Loading />;
  if (map.error) return <ErrorNote message={(map.error as Error).message} />;
  if (fields.length === 0) {
    return (
      <Card>
        <CardTitle style={{ marginBottom: 6 }}>Field map</CardTitle>
        <Note>Upload the REDCap data dictionary under Project setup first — the map is built from it.</Note>
      </Card>
    );
  }

  const edit = (next: DraftRow[]) => {
    setSavedNote(null);
    setDraft(next);
  };
  const mapped = new Set(rows.map((r) => r.variable));
  const addable = fields.filter(
    (f) => f.form === (addForm || forms[0]?.name) && MAPPABLE.has(f.type) && f.variable !== recordId && !mapped.has(f.variable),
  );

  async function runSuggest() {
    const r = await suggest.mutateAsync({ forms: suggestForms });
    const fresh = r.rows.filter((s) => !mapped.has(s.variable));
    edit([...rows, ...fresh.map((s) => ({ variable: s.variable, source: s.source, valueText: '' }))]);
  }

  function onSave() {
    const body: MapInputRow[] = rows.map((r) => ({
      variable: r.variable,
      source: r.source,
      valueMap: parseValueText(r.valueText),
    }));
    save.mutate(
      { rows: body },
      { onSuccess: (d) => setSavedNote(`Saved ${num(d.rows.length)} ${d.rows.length === 1 ? 'field' : 'fields'}.`) },
    );
  }

  const unmappedSource = rows.some((r) => !r.source);

  return (
    <Card pad={0}>
      <div style={{ padding: '18px 16px 12px' }}>
        <Eyebrow>Setup · the lab’s</Eyebrow>
        <CardTitle style={{ marginBottom: 4 }}>Field map</CardTitle>
        <Note>
          Which CRM field fills which REDCap field. Choices are sent as their codes: a CRM value matching a
          choice’s label or code needs nothing more; anything else takes a line like{' '}
          <code style={{ fontFamily: MONO }}>Woman = 1</code>, and{' '}
          <code style={{ fontFamily: MONO }}>* = 4</code> catches the rest. A REDCap field the website does not
          ask about simply stays blank.
        </Note>
        <div style={{ marginTop: 10 }}>
          <Pill tone={{ bg: color.tint2, fg: color.navy }}>Shared by every study</Pill>
          {!canWrite && (
            <span style={{ fontSize: 12, color: color.muted, marginLeft: 8 }}>
              Changing it takes the REDCap permission and access to all studies.
            </span>
          )}
        </div>
      </div>

      {canWrite && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            padding: '0 16px 14px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 260, flex: 1 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: color.muted }}>
              Suggest from the dictionary, for the forms the website fills
            </span>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {forms.map((f) => {
                const on = suggestForms.includes(f.name);
                return (
                  <button
                    key={f.name}
                    onClick={() =>
                      setSuggestForms(on ? suggestForms.filter((x) => x !== f.name) : [...suggestForms, f.name])
                    }
                    aria-pressed={on}
                    style={{
                      font: 'inherit',
                      fontSize: 12,
                      fontWeight: 700,
                      padding: '5px 11px',
                      borderRadius: 999,
                      cursor: 'pointer',
                      border: `1px solid ${on ? color.navy : color.line}`,
                      background: on ? color.navy : color.white,
                      color: on ? color.white : color.ink,
                      fontFamily: MONO,
                    }}
                  >
                    {f.name}
                  </button>
                );
              })}
            </div>
          </div>
          <Button
            variant="soft"
            onClick={() => void runSuggest()}
            disabled={suggest.isPending || suggestForms.length === 0}
            title={suggestForms.length === 0 ? 'Pick the forms the website fills' : undefined}
          >
            {suggest.isPending ? 'Suggesting…' : 'Suggest'}
          </Button>
        </div>
      )}
      {suggest.error && (
        <div style={{ padding: '0 16px 12px' }}>
          <ProblemNote problem={problemOf(suggest.error)} />
        </div>
      )}

      <div className="x-scroll">
        <div style={{ minWidth: 820 }}>
          <div style={{ display: 'grid', gridTemplateColumns: MAP_GRID, gap: 12, padding: '0 16px 8px' }}>
            <ColHead>REDCap field</ColHead>
            <ColHead>Filled from</ColHead>
            <ColHead>Values</ColHead>
            <span />
          </div>
          {rows.length === 0 && (
            <p style={{ padding: '12px 16px 16px', margin: 0, fontSize: 13, color: color.muted }}>
              Nothing mapped yet. Pick the forms the website fills and press Suggest, or add fields below.
            </p>
          )}
          {rows.map((row, i) => {
            const f: RedcapDictionaryField | undefined = byVar.get(row.variable);
            const src = sourceByKey.get(row.source);
            const choice = f ? CHOICE_TYPES.has(f.type) : false;
            const set = (patch: Partial<DraftRow>) => edit(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
            return (
              <div
                key={row.variable}
                style={{
                  display: 'grid',
                  gridTemplateColumns: MAP_GRID,
                  gap: 12,
                  alignItems: 'start',
                  padding: '10px 16px',
                  borderTop: `1px solid ${color.line}`,
                  fontSize: 12.5,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <code style={{ fontFamily: MONO, fontWeight: 700, color: color.navy, wordBreak: 'break-all' }}>
                    {row.variable}
                  </code>
                  {f ? (
                    <div style={{ fontSize: 11.5, color: color.muted }}>
                      {f.form} · {f.type}
                      {f.validation ? ` · ${f.validation}` : ''} — {f.label}
                    </div>
                  ) : (
                    <div style={{ marginTop: 4 }}>
                      <Pill tone={{ bg: '#f6d0c4', fg: '#953a26' }}>Not in the current dictionary</Pill>
                    </div>
                  )}
                </div>
                <div>
                  <select
                    aria-label={`CRM field for ${row.variable}`}
                    value={row.source}
                    disabled={!canWrite}
                    onChange={(e) => set({ source: e.target.value })}
                    style={{ ...selectStyle, fontSize: 12.5, padding: '6px 28px 6px 10px' }}
                  >
                    <option value="">Choose a CRM field…</option>
                    {sourceGroups(sources).map(([label, list]) =>
                      list.length ? (
                        <optgroup key={label} label={label}>
                          {list.map((s) => (
                            <option key={s.key} value={s.key}>
                              {s.label}
                            </option>
                          ))}
                        </optgroup>
                      ) : null,
                    )}
                  </select>
                  {src?.values && (
                    <div style={{ fontSize: 11, color: color.muted, marginTop: 4 }}>Can be: {src.values.join(' · ')}</div>
                  )}
                </div>
                <div>
                  {choice || row.valueText ? (
                    <textarea
                      aria-label={`Value map for ${row.variable}`}
                      value={row.valueText}
                      disabled={!canWrite}
                      onChange={(e) => set({ valueText: e.target.value })}
                      rows={Math.min(4, Math.max(1, row.valueText.split('\n').length))}
                      placeholder={choice ? 'Only where labels differ, e.g. Woman = 1' : ''}
                      style={{ ...inputStyle, fontFamily: MONO, fontSize: 12, padding: '6px 10px', resize: 'vertical' }}
                    />
                  ) : (
                    <span style={{ color: color.muted }}>As written{f?.validation ? `, shaped for ${f.validation}` : ''}</span>
                  )}
                  {f?.choices && (
                    <div style={{ fontSize: 11, color: color.muted, marginTop: 4 }}>
                      Codes: {f.choices.map((c) => `${c.code} ${c.label}`).join(' · ')}
                    </div>
                  )}
                </div>
                <div>
                  {canWrite && (
                    <button
                      onClick={() => edit(rows.filter((_, j) => j !== i))}
                      aria-label={`Remove ${row.variable}`}
                      title="Remove"
                      className="hov-tint"
                      style={{
                        border: `1px solid ${color.line}`,
                        background: color.white,
                        color: color.muted,
                        borderRadius: 8,
                        cursor: 'pointer',
                        padding: '4px 9px',
                        fontSize: 13,
                        fontWeight: 700,
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {canWrite && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            padding: '12px 16px 16px',
            borderTop: `1px solid ${color.line}`,
          }}
        >
          <Field label="Add a field from" style={{ width: 220 }}>
            <select
              value={addForm || forms[0]?.name || ''}
              onChange={(e) => {
                setAddForm(e.target.value);
                setAddVar('');
              }}
              style={selectStyle}
            >
              {forms.map((f) => (
                <option key={f.name} value={f.name}>
                  {f.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Field" style={{ width: 300, maxWidth: '100%' }}>
            <select value={addVar} onChange={(e) => setAddVar(e.target.value)} style={selectStyle}>
              <option value="">Choose…</option>
              {addable.map((f) => (
                <option key={f.variable} value={f.variable}>
                  {f.variable} — {f.label.slice(0, 50)}
                </option>
              ))}
            </select>
          </Field>
          <Button
            variant="ghost"
            disabled={!addVar}
            onClick={() => {
              edit([...rows, { variable: addVar, source: '', valueText: '' }]);
              setAddVar('');
            }}
          >
            Add
          </Button>
          <span style={{ flex: 1 }} />
          {dirty && (
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Discard changes
            </Button>
          )}
          <Button
            onClick={onSave}
            disabled={!dirty || save.isPending || unmappedSource}
            title={unmappedSource ? 'Every row needs a CRM field' : undefined}
          >
            {save.isPending ? 'Saving…' : 'Save map'}
          </Button>
        </div>
      )}
      {(save.error || (map.data && map.data.problems.length > 0 && !dirty) || savedNote) && (
        <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {save.error && <ProblemNote problem={problemOf(save.error)} />}
          {!dirty && map.data && map.data.problems.length > 0 && (
            <ProblemNote
              problem={{
                message:
                  map.data.problems.length === 1
                    ? map.data.problems[0]!
                    : 'The saved map needs fixing before an export can be made:',
                problems: map.data.problems.length > 1 ? map.data.problems : undefined,
              }}
            />
          )}
          {savedNote && (
            <div role="status" style={{ fontSize: 13, fontWeight: 600, color: color.green }}>
              {savedNote}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

const ISSUE_GRID = 'minmax(0,1.6fr) repeat(3, minmax(0,0.7fr))';

function ExportPanel({ slug, name }: { slug: string; name: string }) {
  const meta = useMeta();
  const [filter, setFilter] = useState<ExportFilterInput>(DEFAULT_EXPORT_FILTER);
  const plan = useExportPlan(slug, filter);
  const download = useDownloadExport();
  const [saved, setSaved] = useState<string | null>(null);

  const p = plan.data?.plan;
  const ready = p && p.problems.length === 0 && p.count > 0;

  function onDownload() {
    setSaved(null);
    download.mutate({ slug, filter }, { onSuccess: (file) => setSaved(file) });
  }

  return (
    <Card pad={0}>
      <div style={{ padding: '18px 16px 12px' }}>
        <CardTitle style={{ marginBottom: 4 }}>Export sign-ups for REDCap</CardTitle>
        <Note>
          The file carries names and contact details, so it is logged and only for people who may read them.
          Upload it straight into REDCap — opening and re-saving it in a spreadsheet can change dates and codes.
        </Note>
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', padding: '0 16px 14px' }}>
        <Field label="Form" style={{ width: 190 }}>
          <select
            value={filter.form}
            onChange={(e) => setFilter({ ...filter, form: e.target.value as ExportFilterInput['form'] })}
            style={selectStyle}
          >
            <option value="intake">Intake (self-referral)</option>
            <option value="referral">Clinician referral</option>
          </select>
        </Field>
        <Field label="Status" style={{ width: 210 }}>
          <select
            value={filter.status ?? ''}
            onChange={(e) => setFilter({ ...filter, status: e.target.value || null })}
            style={selectStyle}
          >
            <option value="">Any status</option>
            {(meta.data?.statuses ?? []).map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Signed up on or after" style={{ width: 190 }}>
          <input
            type="date"
            value={filter.since ?? ''}
            onChange={(e) => setFilter({ ...filter, since: e.target.value || null })}
            style={inputStyle}
          />
        </Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, color: color.ink, paddingBottom: 10 }}>
          <input
            type="checkbox"
            checked={filter.onlyNew}
            onChange={(e) => setFilter({ ...filter, onlyNew: e.target.checked })}
          />
          Only sign-ups not exported before
        </label>
      </div>

      <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {plan.isLoading && <Loading />}
        {plan.error && <ErrorNote message={(plan.error as Error).message} />}
        {p && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: color.navy }}>
                  {num(p.count)} {p.count === 1 ? 'sign-up' : 'sign-ups'} for {name}
                  {p.columns.length > 0 ? ` · ${num(p.columns.length)} columns` : ''}
                </p>
                {p.truncated && <Note>More than 5,000 match — narrow the filters and export in parts.</Note>}
              </div>
              <Button onClick={onDownload} disabled={!ready || download.isPending}>
                <IconDownload /> {download.isPending ? 'Preparing…' : 'Download CSV'}
              </Button>
            </div>
            {p.problems.length > 0 && (
              <ProblemNote
                problem={{
                  message: p.problems.length === 1 ? p.problems[0]! : 'This export is not ready:',
                  problems: p.problems.length > 1 ? p.problems : undefined,
                }}
              />
            )}
            {p.problems.length === 0 && p.count === 0 && (
              <Note>{filter.onlyNew ? 'Everyone matching these filters has been exported already.' : 'No sign-ups match these filters.'}</Note>
            )}
            {p.issues.length > 0 && (
              <div style={{ border: `1px solid ${color.line}`, borderRadius: 14, overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: ISSUE_GRID, gap: 12, padding: '9px 14px', background: color.bg }}>
                  <ColHead>Field</ColHead>
                  <ColHead>Blank</ColHead>
                  <ColHead>No matching code</ColHead>
                  <ColHead>Not valid</ColHead>
                </div>
                {p.issues.map((i) => (
                  <div
                    key={i.variable}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: ISSUE_GRID,
                      gap: 12,
                      padding: '8px 14px',
                      borderTop: `1px solid ${color.line}`,
                      fontSize: 12.5,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    <code style={{ fontFamily: MONO, color: color.navy }}>{i.variable}</code>
                    <span style={{ color: color.muted }}>{i.blank || '—'}</span>
                    <span style={{ color: i.unmatched ? '#8a4f16' : color.muted, fontWeight: i.unmatched ? 800 : 400 }}>
                      {i.unmatched || '—'}
                    </span>
                    <span style={{ color: i.invalid ? '#8a4f16' : color.muted, fontWeight: i.invalid ? 800 : 400 }}>
                      {i.invalid || '—'}
                    </span>
                  </div>
                ))}
                <p style={{ margin: 0, padding: '8px 14px 10px', fontSize: 11.5, color: color.muted, borderTop: `1px solid ${color.line}` }}>
                  Those cells go out blank. Blank means the sign-up did not say; no matching code needs a value-map
                  line; not valid means REDCap’s validation would refuse it (a phone that is not a US number, say).
                </p>
              </div>
            )}
          </>
        )}
        {download.error && <ProblemNote problem={problemOf(download.error)} />}
        {saved && (
          <div
            role="status"
            style={{ background: '#cfe9d8', color: '#14563c', borderRadius: 14, padding: '12px 16px', fontSize: 13, fontWeight: 600 }}
          >
            Saved {saved}. In REDCap: Applications → Data Import Tool → upload the CSV. Tick “Force record
            auto-numbering” so REDCap gives each new person their record number, and keep the date format as
            YYYY-MM-DD.
          </div>
        )}
      </div>

      {plan.data && plan.data.history.length > 0 && (
        <div style={{ borderTop: `1px solid ${color.line}`, padding: '14px 16px 16px' }}>
          <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: color.muted }}>
            Recent exports
          </p>
          {plan.data.history.map((h) => (
            <p key={h.id} style={{ margin: '0 0 4px', fontSize: 12.5, color: color.ink }}>
              {dateTime(h.createdAt)} · {num(h.rows)} {h.form === 'referral' ? 'referral' : 'intake'}{' '}
              {h.rows === 1 ? 'sign-up' : 'sign-ups'}
              {h.exportedByName ? ` · ${h.exportedByName}` : ''}
            </p>
          ))}
        </div>
      )}
    </Card>
  );
}

/** `canWriteSetup` edits the map every study shares, which also takes access to all studies. */
export function RedcapExport({
  slug,
  name,
  canWriteSetup,
  canExport,
  onGo,
}: {
  slug: string;
  name: string;
  canWriteSetup: boolean;
  canExport: boolean;
  onGo: (tab: RedcapTab) => void;
}) {
  const setup = useRedcapSetup();
  const dict = useDictionary(slug);
  // Without the project or the dictionary there is nothing to write the file in: one pointer, not two half-working cards.
  const missing: 'project' | 'dictionary' | null = !setup.data || !dict.data
    ? null
    : dict.data.project === null
      ? 'project'
      : setup.data.summary.fields === 0
        ? 'dictionary'
        : null;
  const noDictionary = missing !== null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {missing && (
        <PointerCard
          title={missing === 'project' ? `Link ${name} to its REDCap project first` : 'Upload the data dictionary first'}
          actionLabel={canWriteSetup ? 'Go to Project setup' : undefined}
          onAction={canWriteSetup ? () => onGo('setup') : undefined}
        >
          {missing === 'project'
            ? 'The file names the REDCap event a sign-up belongs to, which is part of the project link. Step 1, under Project setup.'
            : 'The file is written in the REDCap project’s own variable names and answer codes, and those come from the dictionary. Step 1, under Project setup.'}
        </PointerCard>
      )}
      <GuideCard
        storageKey="enact.redcap.guide.export"
        intro="Accepted sign-ups, written in the REDCap project’s own variables, for a coordinator to upload with REDCap’s Data Import Tool. The file carries names and contact details, so downloading it is logged as a PHI access."
        steps={[
          {
            do: 'Check the field map once.',
            where: (
              <Where at="here">
                the map below says which CRM answer fills which REDCap variable, with codes for choice fields. It is
                the lab’s, shared by every study; Suggest proposes one from the dictionary.
              </Where>
            ),
          },
          {
            do: 'Pick who goes.',
            where: (
              <Where at="here">
                form, status, signed up since, and “only sign-ups not exported before” so nobody goes twice. The
                plan says how many, and which cells will go out blank or be refused by REDCap’s validation.
              </Where>
            ),
          },
          {
            do: 'Download the CSV.',
            where: (
              <Where at="here">
                Download CSV. Do not open and re-save it in a spreadsheet — that can change dates and codes.
              </Where>
            ),
          },
          {
            do: 'Import it in REDCap.',
            where: (
              <Where at="REDCap">
                Applications → Data Import Tool → upload the file, tick “Force record auto-numbering”, keep the
                date format YYYY-MM-DD, review, Import. Cards on the board then read <em>Exported</em> with the date.
              </Where>
            ),
          },
        ]}
      />
      {!noDictionary &&
        (canExport ? (
          <ExportPanel slug={slug} name={name} />
        ) : (
          <Card>
            <Note>Downloading an export needs permission to export submissions and to read patient details.</Note>
          </Card>
        ))}
      {!noDictionary && <MapEditor canWrite={canWriteSetup} />}
    </div>
  );
}
